import { useState, useEffect } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, Bell, MessageSquare, Pencil, MoreHorizontal, X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { EventForm } from './EventForm';
import { EventEditForm } from './EventEditForm';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { sendSmsReminder, fetchUserEvents, deleteEvent, mergeEvents } from '@/services/calendarService';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CalendarRemindersList } from './CalendarRemindersList';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";

export type EventType = 'task' | 'meeting' | 'reminder' | 'personal';

export interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  type: EventType;
  reminder?: boolean;
  reminderTime?: string;
  reminderText?: string;
  smsReminder?: boolean;
  smsReminderTime?: string;
  phoneNumber?: string;
  completed?: boolean;
  linkedTaskId?: string;
}

export interface CalendarProps {
  onClose?: () => void;
  showRemindersView?: boolean;
  onToggleView?: () => void;
}

export function Calendar({ onClose, showRemindersView = false, onToggleView }: CalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarEvent[]>([]);
  const [isDayEventsDialogOpen, setIsDayEventsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  // Load events from both local storage and Supabase when component mounts
  useEffect(() => {
    const loadEvents = async () => {
      setIsLoading(true);
      try {
        // First, load from local storage
        const localEventsString = localStorage.getItem('calendar-events');
        let localEvents: CalendarEvent[] = [];
        
        if (localEventsString) {
          try {
            localEvents = JSON.parse(localEventsString);
            console.log(`Loaded ${localEvents.length} events from local storage`);
          } catch (err) {
            console.error('Error parsing local calendar events:', err);
            localEvents = [];
          }
        }
        
        // Initialize with local events
        setEvents(localEvents);
        
        // If user is authenticated, fetch events from Supabase and merge
        if (user) {
          console.log('Fetching events from Supabase for user:', user.id);
          try {
            const supabaseEvents = await fetchUserEvents();
            if (supabaseEvents && supabaseEvents.length > 0) {
              console.log(`Found ${supabaseEvents.length} events in Supabase`);
              const merged = mergeEvents(localEvents, supabaseEvents);
              console.log(`Merged to ${merged.length} total events`);
              
              // Update local storage with merged events
              localStorage.setItem('calendar-events', JSON.stringify(merged));
              
              // Update state with merged events
              setEvents(merged);
              
              toast({
                title: "Calendar Synced",
                description: `Loaded ${merged.length} calendar events`,
              });
            } else if (localEvents.length > 0) {
              console.log('No events found in Supabase, using local events only');
            }
          } catch (error) {
            console.error('Error loading events from Supabase:', error);
            toast({
              title: "Sync Error",
              description: "Failed to load events from your account",
              variant: "destructive"
            });
          }
        } else {
          console.log('User not authenticated, using local events only');
        }
      } catch (error) {
        console.error('Error in loadEvents:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadEvents();
    
    // Listen for events from other components
    window.addEventListener('calendar-events-updated', () => loadEvents());
    
    return () => {
      window.removeEventListener('calendar-events-updated', () => loadEvents());
    };
  }, [user]); // Re-run when user auth status changes

  const saveEvents = (updatedEvents: CalendarEvent[]) => {
    try {
      localStorage.setItem('calendar-events', JSON.stringify(updatedEvents));
      setEvents(updatedEvents);
      
      const event = new CustomEvent('calendar-events-updated');
      window.dispatchEvent(event);
      
      if (user) {
        syncEventsToSupabase(updatedEvents);
      }
    } catch (error) {
      console.error('Error saving events:', error);
      toast({
        title: "Error",
        description: "Failed to save calendar events",
        variant: "destructive"
      });
    }
  };

  const syncEventsToSupabase = async (events: CalendarEvent[]) => {
    try {
      if (!user) return;
      console.log(`Syncing ${events.length} events to Supabase`);

      for (const event of events) {
        const { data: existingEvent } = await supabase
          .from('calendar_events')
          .select('id')
          .eq('id', event.id)
          .single();

        if (existingEvent) {
          await supabase
            .from('calendar_events')
            .update({
              title: event.title,
              date: event.date,
              type: event.type,
              reminder: event.reminder || false,
              reminder_time: event.reminderTime,
              reminder_text: event.reminderText,
              sms_reminder: event.smsReminder || false,
              sms_reminder_time: event.smsReminderTime,
              phone_number: event.phoneNumber
            })
            .eq('id', event.id);
        } else {
          await supabase
            .from('calendar_events')
            .insert({
              id: event.id,
              user_id: user.id,
              title: event.title,
              date: event.date,
              type: event.type,
              reminder: event.reminder || false,
              reminder_time: event.reminderTime,
              reminder_text: event.reminderText,
              sms_reminder: event.smsReminder || false,
              sms_reminder_time: event.smsReminderTime,
              phone_number: event.phoneNumber
            });
        }
      }
      
      console.log('Sync to Supabase completed successfully');
    } catch (error) {
      console.error('Error syncing events to Supabase:', error);
      toast({
        title: "Sync Error",
        description: "Failed to sync events to your account",
        variant: "destructive"
      });
    }
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const dateFormat = "MMMM yyyy";
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });
  
  const handleDateClick = (day: Date) => {
    setSelectedDate(day);
    const dayEvents = getEventsForDay(day);
    
    if (dayEvents.length > 0) {
      setSelectedDayEvents(dayEvents);
      setIsDayEventsDialogOpen(true);
    } else {
      setIsFormOpen(true);
    }
  };

  const handleCreateEvent = (event: Omit<CalendarEvent, 'id'>) => {
    const newEvent: CalendarEvent = {
      ...event,
      id: crypto.randomUUID(),
    };
    
    const updatedEvents = [...events, newEvent];
    saveEvents(updatedEvents);
    
    if (newEvent.reminder) {
      scheduleReminder(newEvent);
    }
    
    if (newEvent.smsReminder && user) {
      scheduleSmsReminder(newEvent.id);
    }
    
    setIsFormOpen(false);
    toast({
      title: "Event Created",
      description: `${newEvent.title} added for ${format(parseISO(newEvent.date), 'PPP')}`,
    });
  };

  const handleEditEvent = (eventId: string) => {
    setEditingEventId(eventId);
    setIsDayEventsDialogOpen(false);
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteEvent(eventId);
      const updatedEvents = events.filter(event => event.id !== eventId);
      setEvents(updatedEvents);
      
      toast({
        title: "Event Deleted",
        description: "The event has been removed from your calendar",
      });
    } catch (error) {
      console.error('Error deleting event:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete the event",
        variant: "destructive"
      });
    }
  };

  const handleSaveEdit = (updatedEvent: CalendarEvent) => {
    const updatedEvents = events.map(event => 
      event.id === updatedEvent.id ? updatedEvent : event
    );
    
    saveEvents(updatedEvents);
    setEditingEventId(null);
  };

  const scheduleReminder = (event: CalendarEvent) => {
    const reminderDate = event.reminderTime 
      ? new Date(event.reminderTime) 
      : parseISO(event.date);
      
    const now = new Date();
    const timeUntilReminder = reminderDate.getTime() - now.getTime();
    
    if (timeUntilReminder > 0) {
      setTimeout(() => {
        toast({
          title: "Reminder",
          description: event.title,
        });
      }, timeUntilReminder);
    }
  };

  const scheduleSmsReminder = async (eventId: string) => {
    try {
      if (!user) return;

      console.log('Scheduling SMS reminder for event:', eventId);
      const result = await sendSmsReminder(eventId);
      console.log('SMS reminder scheduled:', result);
      
      toast({
        title: "SMS Reminder Scheduled",
        description: "You will receive a text message reminder for this event",
      });
    } catch (error) {
      console.error('Error scheduling SMS reminder:', error);
      toast({
        title: "SMS Reminder Error",
        description: "There was an error scheduling your SMS reminder",
        variant: "destructive"
      });
    }
  };

  const typeToColorClass = (type: EventType, completed?: boolean): string => {
    if (completed) {
      return 'bg-green-200 text-green-800';
    }
    
    switch (type) {
      case 'task':
        return 'bg-blue-200 text-blue-800';
      case 'meeting':
        return 'bg-purple-200 text-purple-800';
      case 'reminder':
        return 'bg-yellow-200 text-yellow-800';
      case 'personal':
        return 'bg-green-200 text-green-800';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  const getEventsForDay = (day: Date) => {
    return events.filter(event => {
      try {
        const eventDate = parseISO(event.date);
        return isSameDay(eventDate, day);
      } catch (error) {
        console.error('Error parsing date for event:', event);
        return false;
      }
    });
  };

  const EventDropdownMenu = ({ event }: { event: CalendarEvent }) => {
    const isTaskEvent = !!event.linkedTaskId;
    
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-5 w-5 p-0">
            <MoreHorizontal className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isTaskEvent && (
            <DropdownMenuItem onClick={() => handleEditEvent(event.id)}>
              <Pencil className="h-3 w-3 mr-2" />
              Edit
            </DropdownMenuItem>
          )}
          {isTaskEvent && !event.completed && (
            <DropdownMenuItem onClick={() => handleMarkTaskCompleted(event)}>
              <CheckCircle className="h-3 w-3 mr-2" />
              Mark Completed
            </DropdownMenuItem>
          )}
          <DropdownMenuItem 
            onClick={() => handleDeleteEvent(event.id)}
            className="text-destructive"
          >
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const handleMarkTaskCompleted = (event: CalendarEvent) => {
    if (!event.linkedTaskId) return;
    
    const updatedEvents = events.map(e => 
      e.id === event.id ? { ...e, completed: true } : e
    );
    
    saveEvents(updatedEvents);
    
    try {
      const savedTasks = localStorage.getItem('tasks');
      if (savedTasks) {
        const tasks = JSON.parse(savedTasks);
        const updatedTasks = tasks.map((task: any) => 
          task.id === event.linkedTaskId ? { ...task, completed: true, completedAt: new Date().toISOString() } : task
        );
        localStorage.setItem('tasks', JSON.stringify(updatedTasks));
        
        const taskEvent = new CustomEvent('tasks-updated');
        window.dispatchEvent(taskEvent);
      }
    } catch (error) {
      console.error("Error updating task completion status:", error);
    }
    
    toast({
      title: "Task Completed",
      description: `"${event.title}" has been marked as completed`,
    });
  };

  if (showRemindersView) {
    return (
      <CalendarRemindersList 
        events={events}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
        onClose={onToggleView}
      />
    );
  }

  return (
    <div className="glass-panel rounded-xl p-4 space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-primary">Calendar</h2>
        <div className="flex items-center gap-2">
          <div className="flex space-x-1">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={prevMonth}
              className="h-8 w-8"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="flex items-center px-2 font-medium">
              {format(currentMonth, dateFormat)}
            </span>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={nextMonth}
              className="h-8 w-8"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-1">
            {onToggleView && (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={onToggleView}
              >
                <Bell className="h-4 w-4 mr-1" />
                Reminders
              </Button>
            )}
            
            {onClose && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onClose}
                className="h-8 w-8 ml-1"
                aria-label="Close calendar"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-pulse text-center">
            <p>Loading calendar events...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 text-center">
            {days.map(day => (
              <div key={day} className="text-xs font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}

            {calendarDays.map(day => {
              const dayEvents = getEventsForDay(day);
              const hasReminder = dayEvents.some(e => e.reminder);
              const hasSmsReminder = dayEvents.some(e => e.smsReminder);
              
              return (
                <div
                  key={day.toString()}
                  onClick={() => handleDateClick(day)}
                  className={cn(
                    "min-h-[60px] p-1 text-center text-sm border border-border/40 rounded-md flex flex-col",
                    !isSameMonth(day, monthStart) && "text-muted-foreground opacity-50",
                    "hover:bg-accent/50 cursor-pointer transition-colors"
                  )}
                >
                  <div className="flex justify-between items-start">
                    <span className={cn(
                      "text-xs font-medium inline-block w-5 h-5 rounded-full",
                      isSameDay(day, new Date()) && "bg-primary text-primary-foreground"
                    )}>
                      {format(day, 'd')}
                    </span>
                    <div className="flex space-x-1">
                      {hasReminder && (
                        <Bell className="h-3 w-3 text-yellow-500" />
                      )}
                      {hasSmsReminder && (
                        <MessageSquare className="h-3 w-3 text-blue-500" />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 mt-1 overflow-hidden">
                    {dayEvents.slice(0, 2).map((event) => (
                      <div 
                        key={event.id}
                        className={cn(
                          "text-[10px] truncate rounded px-1 py-0.5 flex justify-between items-center",
                          typeToColorClass(event.type, event.completed)
                        )}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the day click
                        }}
                      >
                        <span className="truncate flex-1">{event.title}</span>
                        <EventDropdownMenu event={event} />
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-[10px] text-muted-foreground">
                        +{dayEvents.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={isDayEventsDialogOpen} onOpenChange={setIsDayEventsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              <span>
                Events for {selectedDate ? format(selectedDate, 'MMMM d, yyyy') : ''}
              </span>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsDayEventsDialogOpen(false);
                  setIsFormOpen(true);
                }}
              >
                + Add Event
              </Button>
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto">
            {selectedDayEvents.length > 0 ? (
              <div className="space-y-2">
                {selectedDayEvents.map(event => (
                  <div
                    key={event.id}
                    className={cn(
                      "p-2 rounded-md flex flex-col",
                      typeToColorClass(event.type, event.completed)
                    )}
                  >
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-sm">{event.title}</h4>
                      <EventDropdownMenu event={event} />
                    </div>
                    
                    {event.reminderText && (
                      <p className="text-xs mt-1 break-words">{event.reminderText}</p>
                    )}
                    
                    {event.reminderTime && (
                      <p className="text-xs opacity-80 mt-1">
                        <span className="font-medium">Reminder:</span> {format(new Date(event.reminderTime), 'h:mm a')}
                      </p>
                    )}
                    
                    {event.smsReminder && event.smsReminderTime && (
                      <p className="text-xs opacity-80 mt-1">
                        <span className="font-medium">SMS Reminder:</span> {format(new Date(event.smsReminderTime), 'h:mm a')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-4 text-muted-foreground">No events for this day</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {isFormOpen && selectedDate && (
        <EventForm 
          date={selectedDate} 
          onSubmit={(event) => {
            handleCreateEvent(event);
            setSelectedDate(null);
          }}
          onCancel={() => {
            setIsFormOpen(false);
            setSelectedDate(null);
          }}
        />
      )}

      {editingEventId && (
        <EventEditForm
          eventId={editingEventId}
          onSave={(updatedEvent) => {
            handleSaveEdit(updatedEvent);
            setEditingEventId(null);
          }}
          onCancel={() => setEditingEventId(null)}
        />
      )}
    </div>
  );
}
